import { z } from 'zod/v4';

import type { InfinexMessageNamespace } from './base';
import { generateEventSchema } from './schemaBuilder';

export const ServerResponseMethods = [
  'evm:signMessage',
  'svm:signMessage',

  'evm:signTypedData',

  'evm:signTransaction',
  'svm:signTransaction',

  'evm:sendTransaction',
  'svm:sendTransaction',

  'svm:signBatchTransactions',
] as const;

export const OperationsServerResponseMethods = ['swidge-result'] as const;
export type ServerResponseMethod = (typeof ServerResponseMethods)[number];
export type OperationsServerResponseMethod =
  (typeof OperationsServerResponseMethods)[number];

const namespace: InfinexMessageNamespace = 'server-response';

// ───────────────────────── evm:signMessage ─────────────────────────
export const evmSignMessageSchema = generateEventSchema({
  namespace,
  method: 'evm:signMessage',

  requestData: z.never(),
  responseData: z.never(),
  notificationData: z
    .object({
      txnRequestId: z.string(),
      response: z.string(),
    })
    .strict(),
});

// ───────────────────────── evm:signMessage ─────────────────────────
export const evmSignTypedDataSchema = generateEventSchema({
  namespace,
  method: 'evm:signTypedData',

  requestData: z.never(),
  responseData: z.never(),
  notificationData: z
    .object({
      txnRequestId: z.string(),
      response: z.string(),
    })
    .strict(),
});

// ───────────────────────── svm:signMessage ─────────────────────────
export const svmSignMessageSchema = generateEventSchema({
  namespace,
  method: 'svm:signMessage',

  requestData: z.never(),
  responseData: z.never(),
  notificationData: z
    .object({
      txnRequestId: z.string(),
      response: z.string(),
    })
    .strict(),
});

// ─────────────────────── evm:signTransaction ────────────────────────
export const evmSignTransactionSchema = generateEventSchema({
  namespace,
  method: 'evm:signTransaction',

  requestData: z.never(),
  responseData: z.never(),
  notificationData: z
    .object({
      txnRequestId: z.string(),
      response: z.string(),
    })
    .strict(),
});

// ─────────────────────── svm:signTransaction ────────────────────────
export const svmSignTransactionSchema = generateEventSchema({
  namespace,
  method: 'svm:signTransaction',

  requestData: z.never(),
  responseData: z.never(),
  notificationData: z
    .object({
      txnRequestId: z.string(),
      response: z.string(),
    })
    .strict(),
});

/**
 * ───────────────────────── evm:sendTransaction ─────────────────────────
 * Server → Channel version
 */
export const evmSendTransactionSchema = generateEventSchema({
  namespace,
  method: 'evm:sendTransaction',
  requestData: z.never(),
  responseData: z.never(),
  notificationData: z.object({
    txnRequestId: z.string(),
    response: z.string().nullish(),
    error: z
      .object({
        code: z.string(),
      })
      .nullish(),
  }),
});

/**
 * ───────────────────────── svm:sendTransaction ─────────────────────────
 * Server → Channel version
 */
export const svmSendTransactionSchema = generateEventSchema({
  namespace,
  method: 'svm:sendTransaction',
  requestData: z.never(),
  responseData: z.never(),
  notificationData: z.object({
    txnRequestId: z.string(),
    response: z.string().nullish(),
    error: z
      .object({
        code: z.string(),
      })
      .nullish(),
  }),
});

/**
 * ──────────────────── svm:signBatchTransactions ───────────────────────
 * Server → Channel version
 */
export const svmSignBatchTransactionsSchema = generateEventSchema({
  namespace,
  method: 'svm:signBatchTransactions',

  requestData: z.never(),
  responseData: z.never(),
  notificationData: z
    .object({
      txnRequestId: z.string(),
      response: z.array(z.string()),
    })
    .strict(),
});

/**
 * ──────────────────── Swidging ───────────────────────
 * Server → Channel version
 */
export const swidgeTransactionResult = generateEventSchema({
  namespace,
  method: 'swidge-result',

  requestData: z.never(),
  responseData: z.never(),
  notificationData: z
    .object({
      swidgeRequestId: z.string(),
      response: z.boolean(),
    })
    .strict(),
});

/* ─────────────────── Aggregated schema arrays (notification-only) ─────────────────── */
export const serverResponseNotificationParamSchemas = [
  evmSignMessageSchema.notificationParamsSchema,
  svmSignMessageSchema.notificationParamsSchema,
  evmSignTypedDataSchema.notificationParamsSchema,
  evmSignTransactionSchema.notificationParamsSchema,
  svmSignTransactionSchema.notificationParamsSchema,
  evmSendTransactionSchema.notificationParamsSchema,
  svmSendTransactionSchema.notificationParamsSchema,
  svmSignBatchTransactionsSchema.notificationParamsSchema,
  swidgeTransactionResult.notificationParamsSchema,
] as const;

export const serverResponseNotificationEventSchemas = [
  evmSignMessageSchema.notificationSchema,
  evmSignTypedDataSchema.notificationSchema,
  svmSignMessageSchema.notificationSchema,
  evmSignTransactionSchema.notificationSchema,
  svmSignTransactionSchema.notificationSchema,
  evmSendTransactionSchema.notificationSchema,
  svmSendTransactionSchema.notificationSchema,
  svmSignBatchTransactionsSchema.notificationSchema,
  swidgeTransactionResult.notificationSchema,
] as const;
